import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { CalendarDays, FileSpreadsheet, FileText, FileDown, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

const STATUS_COLOR = {
  present: "hsl(var(--status-present))",
  halfday: "hsl(var(--status-halfday))",
  absent:  "hsl(var(--status-absent))",
  leave:   "hsl(var(--status-leave))",
};

const LETTER = { present: "P", halfday: "H", absent: "A", leave: "L" };

function DatePickerBtn({ value, onChange, testid, label }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            data-testid={testid}
            className="h-10 justify-start rounded-md gap-2 w-[180px]"
          >
            <CalendarDays className="h-4 w-4" />
            <span className="font-mono-mini tabular-nums text-xs">{format(value, "MMM d, yyyy")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ReportsPage() {
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // "csv" | "xlsx" | "pdf"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setEmployees(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const loadReport = async () => {
    if (endDate < startDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: isoDate(startDate),
        end_date: isoDate(endDate),
      });
      if (employeeId && employeeId !== "all") params.set("employee_id", employeeId);
      const { data } = await api.get(`/attendance/report?${params.toString()}`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, []);

  const download = async (format_) => {
    if (endDate < startDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    setExporting(format_);
    try {
      const params = new URLSearchParams({
        start_date: isoDate(startDate),
        end_date: isoDate(endDate),
        format: format_,
      });
      if (employeeId && employeeId !== "all") params.set("employee_id", employeeId);
      const res = await fetch(`${API}/attendance/export?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        let msg = "Export failed";
        try {
          const j = await res.json();
          msg = j.detail || msg;
        } catch (parseErr) {
          // Response body wasn't JSON — keep the generic error message.
          console.debug("Export error body was not JSON:", parseErr?.message || parseErr);
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format_ === "xlsx" ? "xlsx" : format_ === "pdf" ? "pdf" : "csv";
      a.download = `attendance_${isoDate(startDate)}_to_${isoDate(endDate)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format_.toUpperCase()} report`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally { setExporting(null); }
  };

  const totals = useMemo(() => {
    const t = { present: 0, halfday: 0, absent: 0, leave: 0, unmarked: 0 };
    if (!report) return t;
    for (const row of report.rows) {
      t.present += row.counts.present;
      t.halfday += row.counts.halfday;
      t.absent  += row.counts.absent;
      t.leave   += row.counts.leave;
      t.unmarked += row.unmarked;
    }
    return t;
  }, [report]);

  const days = report?.days || [];
  const showPerDay = days.length <= 45;

  return (
    <div className="p-8 lg:p-10" data-testid="reports-page">
      <div>
        <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Reports</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Attendance report</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Pick a date range, then preview or export as Excel, PDF, or CSV.
        </p>
      </div>

      {/* Filters */}
      <Card className="mt-8 p-5 rounded-2xl border-border shadow-soft">
        <div className="flex flex-wrap items-end gap-4">
          <DatePickerBtn value={startDate} onChange={setStartDate} testid="report-start-date" label="From" />
          <DatePickerBtn value={endDate}   onChange={setEndDate}   testid="report-end-date"   label="To" />

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="report-employee-filter" className="h-10 w-[220px] rounded-md">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1" />

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={loadReport}
              disabled={loading}
              className="h-10 btn-lift"
              data-testid="run-report-btn"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Run report
            </Button>
            <Button
              variant="outline"
              onClick={() => download("xlsx")}
              disabled={exporting !== null || !report}
              className="h-10 btn-lift"
              data-testid="export-xlsx-btn"
            >
              {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => download("pdf")}
              disabled={exporting !== null || !report}
              className="h-10 btn-lift"
              data-testid="export-pdf-btn"
            >
              {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => download("csv")}
              disabled={exporting !== null || !report}
              className="h-10 btn-lift"
              data-testid="export-csv-btn"
            >
              {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
              CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Totals */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          {[
            { k: "present", label: "Present" },
            { k: "halfday", label: "Half day" },
            { k: "absent",  label: "Absent" },
            { k: "leave",   label: "Leave" },
            { k: "unmarked",label: "Unmarked" },
          ].map(({ k, label }) => (
            <Card key={k} className="p-4 rounded-2xl border-border shadow-soft" data-testid={`report-total-${k}`}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
              <div
                className="font-display text-3xl font-bold tabular-nums tracking-tight mt-2"
                style={{ color: STATUS_COLOR[k] || "hsl(var(--foreground))" }}
              >
                {totals[k]}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="mt-6 border border-border rounded-md bg-card overflow-hidden">
        {loading && !report ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Building report…
          </div>
        ) : !report || report.rows.length === 0 ? (
          <div className="py-14 text-center">
            <Users className="h-6 w-6 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">
              No data. Add employees and mark attendance to see the report.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="sticky left-0 bg-secondary/60 min-w-[200px]">Employee</TableHead>
                  {showPerDay ? days.map((d) => (
                    <TableHead
                      key={d}
                      className="text-center whitespace-nowrap font-mono-mini text-[10px]"
                      data-testid={`report-day-header-${d}`}
                    >
                      {d.slice(5)}
                    </TableHead>
                  )) : null}
                  <TableHead className="text-center">P</TableHead>
                  <TableHead className="text-center">H</TableHead>
                  <TableHead className="text-center">A</TableHead>
                  <TableHead className="text-center">L</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.map((row) => (
                  <TableRow
                    key={row.employee_id}
                    data-testid={`report-row-${row.employee_id}`}
                    className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                  >
                    <TableCell className="sticky left-0 bg-card">
                      <div className="text-sm font-medium">{row.name}</div>
                      <div className="text-[11px] text-muted-foreground">{row.department} · {row.designation}</div>
                    </TableCell>
                    {showPerDay ? row.per_day.map((s, i) => (
                      <TableCell
                        key={`${row.employee_id}-${days[i]}`}
                        className="text-center font-mono-mini text-xs font-bold"
                        style={s ? { color: STATUS_COLOR[s], background: `${STATUS_COLOR[s]}12` } : { color: "hsl(var(--muted-foreground))" }}
                      >
                        {s ? LETTER[s] : "·"}
                      </TableCell>
                    )) : null}
                    <TableCell className="text-center tabular-nums text-sm" style={{ color: STATUS_COLOR.present }}>
                      {row.counts.present}
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm" style={{ color: STATUS_COLOR.halfday }}>
                      {row.counts.halfday}
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm" style={{ color: STATUS_COLOR.absent }}>
                      {row.counts.absent}
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm" style={{ color: STATUS_COLOR.leave }}>
                      {row.counts.leave}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">
                      {row.attendance_rate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {report && showPerDay && (
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-wider text-muted-foreground">
          <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.present }} /> P · Present</span>
          <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.halfday }} /> H · Half day</span>
          <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.absent }} /> A · Absent</span>
          <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.leave }} /> L · Leave</span>
        </div>
      )}
    </div>
  );
}
